import { AlertTriangle, CalendarDays, Clock3 } from "lucide-react";
import { useMemo, useState } from "react";
import { getAppointmentConflicts, getAvailableSlots } from "../lib/availability";
import { combineDateAndTime, formatTime, formatTimeRange, toDateKey } from "../lib/date";
import type { Appointment, Id, Pet } from "../types";

interface AvailabilityPickerProps {
  appointments: Appointment[];
  pets: Pet[];
  date: string;
  startTime: string;
  durationMinutes: number;
  ignoredAppointmentId?: Id;
  dateError?: string;
  timeError?: string;
  onDateChange(date: string): void;
  onTimeChange(time: string): void;
  onDurationChange(durationMinutes: number): void;
}

function slotDayLabel(dateKey: string) {
  const today = toDateKey(new Date());
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (dateKey === today) return "Dziś";
  if (dateKey === toDateKey(tomorrow)) return "Jutro";
  return new Intl.DateTimeFormat("pl-PL", { weekday: "short", day: "numeric", month: "short" }).format(new Date(`${dateKey}T12:00:00`));
}

export function AvailabilityPicker({
  appointments,
  pets,
  date,
  startTime,
  durationMinutes,
  ignoredAppointmentId,
  dateError,
  timeError,
  onDateChange,
  onTimeChange,
  onDurationChange,
}: AvailabilityPickerProps) {
  const now = useMemo(() => new Date(), []);
  const [suggestionStartDate, setSuggestionStartDate] = useState(date);
  const slots = useMemo(
    () => suggestionStartDate
      ? getAvailableSlots(appointments, suggestionStartDate, durationMinutes, now, ignoredAppointmentId)
      : [],
    [appointments, suggestionStartDate, durationMinutes, ignoredAppointmentId, now],
  );
  const conflicts = useMemo(() => {
    if (!date || !startTime) return [];
    const start = combineDateAndTime(date, startTime);
    const end = new Date(start.getTime() + durationMinutes * 60_000);
    return getAppointmentConflicts(appointments, start, end, ignoredAppointmentId);
  }, [appointments, date, startTime, durationMinutes, ignoredAppointmentId]);
  const firstConflict = conflicts[0];
  const conflictPet = firstConflict ? pets.find((pet) => pet.id === firstConflict.petId) : undefined;

  function handleManualDateChange(nextDate: string) {
    onDateChange(nextDate);
    setSuggestionStartDate(nextDate);
  }

  return (
    <section className="form-section availability-picker">
      <div className="section-heading-row">
        <div>
          <span className="section-kicker"><CalendarDays size={15} /> Wybierz termin</span>
          <h2>Termin wizyty</h2>
        </div>
        <label className="duration-field">
          <span>Czas trwania</span>
          <select value={durationMinutes} onChange={(event) => onDurationChange(Number(event.target.value))}>
            <option value={30}>30 min</option>
            <option value={60}>1 godz.</option>
            <option value={90}>1,5 godz.</option>
            <option value={120}>2 godz.</option>
          </select>
        </label>
      </div>

      <div className="availability-title"><Clock3 size={17} /><strong>Najbliższe wolne terminy</strong></div>
      {slots.length > 0 ? (
        <div className="availability-slots">
          {slots.map((slot) => {
            const slotTime = formatTime(slot.startsAt);
            const selected = slot.dateKey === date && slotTime === startTime;
            return (
              <button
                key={slot.startsAt}
                type="button"
                className={`availability-slot ${slot.isWeekend ? "availability-slot--weekend" : ""} ${selected ? "availability-slot--selected" : ""}`}
                onClick={() => {
                  onDateChange(slot.dateKey);
                  onTimeChange(slotTime);
                }}
              >
                <span>{slotDayLabel(slot.dateKey)}</span>
                <strong>{formatTimeRange(slot.startsAt, slot.endsAt)}</strong>
              </button>
            );
          })}
        </div>
      ) : (
        <p className="availability-empty">Brak wolnych slotów w ciągu 7 dni. Wybierz termin ręcznie.</p>
      )}

      <div className="availability-legend" aria-label="Legenda wolnych terminów">
        <span><i className="legend-dot legend-dot--available" /> Dostępny</span>
        <span><i className="legend-dot legend-dot--weekend" /> Weekend</span>
        <span><i className="legend-dot legend-dot--selected" /> Wybrany</span>
      </div>

      <div className="manual-date-grid">
        <label><span>Data *</span><input type="date" value={date} onChange={(event) => handleManualDateChange(event.target.value)} />{dateError && <small className="field-error">{dateError}</small>}</label>
        <label><span>Godzina *</span><input type="time" step="1800" value={startTime} onChange={(event) => onTimeChange(event.target.value)} />{timeError && <small className="field-error">{timeError}</small>}</label>
      </div>

      {firstConflict && (
        <div className="availability-warning" role="status">
          <AlertTriangle size={19} />
          <p>Termin pokrywa się z wizytą {conflictPet?.name || "innego psa"} {formatTimeRange(firstConflict.startsAt, firstConflict.endsAt)}. Możesz mimo to zapisać.</p>
        </div>
      )}
    </section>
  );
}
